import { mapActions, mapGetters, mapState } from 'vuex';

import { getMenu, getSharedMenu } from '@/api/menu';
import ClearCartDialog from '@/components/ClearCartDialog/ClearCartDialog.vue';
import MenuItemOptions from '@/components/MenuItemOptions/MenuItemOptions.vue';
import bus from '@/utils/bus';
import { KEY_CODES, ENTREE_LIMIT, ENTREE_COUNT } from '@/utils/constants';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';

export default {
  name: 'MenuItemDetails',
  components: {
    ClearCartDialog,
    MenuItemOptions,
  },
  props: {
    expectedStartPeriod: {
      default: () => ({}),
      type: Object,
    },
    isScheduled: {
      default: false,
      type: Boolean,
    },
    value: Boolean,
  },
  data() {
    return {
      count: 1,
      enableOrderNote: false,
      entree: {},
      entreeIdentifier: Date.now(),
      entreeLimit: {},
      isOptionsValid: false,
      menuId: null,
      name: '',
      note: '',
      showClearDialog: false,
      subtotal: 0,
      touched: false,
    };
  },
  computed: {
    ...mapGetters('cart', [
      'cartItems',
      'cartMenuId',
      'cartShareKey',
      'isCartEmpty',
    ]),
    ...mapState('menu', {
      cartMenu: ({ cartMenu }) => cartMenu,
    }),
    clearCartData() {
      const selectedOptions = this.entree?.getSelectedOptions(this.entreeIdentifier) || [];
      const options = selectedOptions?.map(({ option, selectItems = [] }) => ({
        index: option?.index,
        select_item_indexes: selectItems?.map(({ index }) => index),
      }));
      return {
        curName: this.name || '',
        item: {
          count: this.count,
          itemIndex: this.entree?.index,
          itemName: this.entree?.name,
          menuId: this.menuId,
          note: this.note,
          options,
          price: this.entree?.price,
          selected_items: this.entree?.formatSelectItem(this.entreeIdentifier) || [],
          share: this.$route?.query?.share,
          subtotal: this.subtotal,
        },
        oriName: this.cartMenu?.infos?.name || '',
      };
    },
    disableAdd() {
      return !Number.isInteger(this.count)
        || this.count === 0
        || (this.hasOptions && !this.isOptionsValid && this.touched);
    },
    hasOptions() {
      return !!this.entree?.options?.length;
    },
    showDivider() {
      return this.hasOptions;
    },
    total() {
      return this.subtotal * this.count;
    },
    hasLimit() {
      return !isNil(this.entreeLimit) && !isEmpty(this.entreeLimit);
    },
    remaining() {
      return this.available - this.count;
    },
    available() {
      const available = this.hasLimit ? this.entreeLimit.available : ENTREE_LIMIT.MAXIMUM;
      return Math.max(available - this.orderedCount, 0);
    },
    countInputMaxLength() {
      return String(this.maxCount).length;
    },
    maxCount() {
      return this.hasLimit ? this.available : ENTREE_COUNT.MAXIMUM;
    },
    minCount() {
      return this.hasLimit ? ENTREE_LIMIT.MINIMUM : ENTREE_COUNT.MINIMUM;
    },
    orderedCount() {
      const { index: itemIndex } = this.entree || {};
      if (isNil(itemIndex)) {
        return 0;
      }
      return this.cartItems
        .filter(({ index }) => itemIndex === index)
        .map(({ count }) => count)
        .reduce((sum, value) => sum + value, 0);
    },
  },
  beforeDestroy() {
    document.removeEventListener('keyup', this.onListenEnter);
  },
  async created() {
    bus.$on('SHOW_MENU_ITEM_DETAILS', async ({
      entree = {},
      menuId,
      name,
      entreeLimit = {},
      enableOrderNote = false,
    }) => {
      this.menuId = menuId;
      this.name = name;

      this.enableOrderNote = enableOrderNote;
      this.entree = entree;
      this.entreeLimit = entreeLimit;
      this.count = this.available ? this.count : 0;
      this.setSubtotal();
    });

    bus.$on('CALCULATE_ENTREE_TOTAL', () => {
      this.setSubtotal();
    });

    document.addEventListener('keyup', this.onListenEnter);

    if (!Object.keys(this.cartMenu)?.length && !this.isCartEmpty && this.cartMenuId) {
      const payload = this.cartShareKey ? {
        cb: getSharedMenu,
        key: this.cartShareKey,
      } : {
        cb: getMenu,
        key: this.cartMenuId,
      };
      await this.getCartMenu(payload);
    }
  },
  methods: {
    ...mapActions('cart', {
      setCartItem: 'SET_CART_ITEM',
    }),
    ...mapActions('menu', {
      getCartMenu: 'GET_CART_MENU',
    }),
    addToCart() {
      if (!this.touched) {
        this.touched = true;
      }
      if (this.hasOptions && !this.isOptionsValid) {
        bus.$emit('VALIDATE_OPTIONS');
        return;
      }
      const selectedOptions = this.entree?.getSelectedOptions(this.entreeIdentifier) || [];
      const options = selectedOptions?.map(({ option, selectItems = [] }) => ({
        index: option?.index,
        select_item_indexes: selectItems?.map(({ index }) => index),
      }));
      const item = {
        count: this.count,
        itemIndex: this.entree?.index,
        itemName: this.entree?.name,
        menuId: this.menuId,
        note: this.note,
        options,
        price: this.entree?.price,
        selected_items: this.entree?.formatSelectItem(this.entreeIdentifier),
        share: this.$route?.query?.share,
        subtotal: this.subtotal,
      };

      if (this.menuId && this.cartMenuId && this.menuId !== this.cartMenuId) {
        this.showClearDialog = true;
        return;
      }

      this.setCartItem(item);
      if (!this.$breakpoints.sm) {
        bus.$emit('SHOW_ADD_TO_CART_ITEM', {
          count: this.count,
          name: this.entree?.name,
          note: this.note,
          price: this.total,
        });
      }
      this.close();
    },
    close() {
      Object.assign(this.$data, this.$options.data.apply(this));
      this.$emit('close');
    },
    countMinus() {
      if (this.count > 1) {
        this.count -= 1;
      } else {
        this.count = 1;
      }
    },
    countPlus() {
      this.count += 1;
    },
    filterOnlyNumberAndLengthSmallerThanMaxLength($event) {
      const keyCode = $event.keyCode ? $event.keyCode : $event.which;
      if (
        ((keyCode < 48 || keyCode > 57)
        && (keyCode !== 8 || keyCode !== 46))
        || $event.target.value.length >= this.countInputMaxLength
      ) {
        $event.preventDefault();
      }
    },
    onOptionsValidation(isValid = false) {
      this.isOptionsValid = isValid;
    },
    onListenEnter(e) {
      if (e.key === 'Escape' || e.keyCode === KEY_CODES.ENTER) {
        this.close();
      }
    },
    setSubtotal() {
      this.subtotal = this.entree?.getTotal(this.entreeIdentifier);
    },
  },
};
